import { all, put, takeEvery, select, delay, takeLatest } from 'redux-saga/effects';
import { find, propEq, has, equals, keys, compose, both, length, prop } from 'ramda';
import qs from 'query-string';
import { FiltersTypes, FiltersActions } from './filters.redux';
import { selectFilterList, selectFirstSelectedMarketId } from './filters.selectors';
import {
  getDataNeededToRestoreLocationFilterState,
  getFiltersQuery,
  getLabelTransformFn,
  getRestoredFiltersState,
} from './filters.utils';
import { isOnHomepage, isOnMarketPage } from '../../shared/utils/location';
import browserHistory from '../../shared/utils/history';
import { selectMarketsWithRegions } from '../market';
import { FilterNames, DEFAULT_FILTER_NAME, DEFAULT_FILTER_LABEL } from './filters.constants';
import { ROUTES } from '../../routes/routes';
import { FADE_IN_UP_TRANSITION_TOTAL_DURATION } from '../../shared/components/animated/animated.constants';

function* afterEverySetFilterDataByFilterName({ filterName, data }) {
  const currentlySelectedMarketId = yield select(selectFirstSelectedMarketId);

  const labelTransformFn = getLabelTransformFn(filterName);
  const label = labelTransformFn(data);

  yield put(FiltersActions.setFilter(filterName, label, data));

  if (filterName === FilterNames.LOCATION) {
    const newSelectedMarketId = yield select(selectFirstSelectedMarketId);

    if (currentlySelectedMarketId !== newSelectedMarketId) {
      yield put(FiltersActions.setFilter(FilterNames.ACTIVITIES, DEFAULT_FILTER_LABEL, DEFAULT_FILTER_LABEL));
    }
  }

  if (isOnMarketPage()) {
    browserHistory.push(ROUTES.filtered);
  }

  if (!isOnHomepage()) {
    const filtersData = yield select(selectFilterList);
    const query = getFiltersQuery(filtersData);
    browserHistory.push(`${ROUTES.filtered}${query}`);
  }
}

function* animateModalByFilterName({ filterName }) {
  if (filterName === DEFAULT_FILTER_NAME) {
    const duration = FADE_IN_UP_TRANSITION_TOTAL_DURATION + 50;
    yield put(FiltersActions.hideModal());
    yield delay(duration);
    yield put(FiltersActions.setCurrentFilterName(filterName));
  } else {
    yield put(FiltersActions.setCurrentFilterName(filterName));
    yield put(FiltersActions.showModal());
  }
}

function* onFiltersSubmit({ query }) {
  const marketsWithRegions = yield select(selectMarketsWithRegions);
  const parsed = qs.parse(query, { arrayFormat: 'bracket' });
  const queryWithOnlyMarket = both(
    has('market'),
    compose(
      equals(1),
      length,
      keys
    )
  )(parsed);

  if (queryWithOnlyMarket) {
    const marketSlug = compose(
      prop('slug'),
      find(propEq('id', parsed.market))
    )(marketsWithRegions);

    browserHistory.push(ROUTES.market(marketSlug));
    return;
  }

  browserHistory.push(`${ROUTES.filtered}${query}`);
}

function* setLocationFilterBasedOnSlug({ slug }) {
  const marketsWithRegions = yield select(selectMarketsWithRegions);
  const market = find(propEq('slug', slug), marketsWithRegions);

  const dataNeededToRestoreFiltersState = {
    [FilterNames.LOCATION]: getDataNeededToRestoreLocationFilterState(marketsWithRegions, market.id, []),
  };

  yield put(FiltersActions.setAllFiltersData(getRestoredFiltersState(dataNeededToRestoreFiltersState)));
}

export default function* watchFilters() {
  yield all([
    takeEvery(FiltersTypes.SET_FILTER_DATA_BY_FILTER_NAME, afterEverySetFilterDataByFilterName),
    takeEvery(FiltersTypes.SET_LOCATION_FILTER_BASED_ON_SLUG, setLocationFilterBasedOnSlug),
    takeLatest(FiltersTypes.ANIMATE_MODAL_BY_FILTER_NAME, animateModalByFilterName),
    takeEvery(FiltersTypes.SUBMIT_FILTERS, onFiltersSubmit),
  ]);
}
